import { DateTime } from 'luxon';
import {
  InfoCard,
  InfoCardVariants,
  Link,
  Progress,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useAnnouncementsRoute } from '../../hooks/useAnnouncementsRoute';
import {
  announcementCreateRouteRef,
  announcementViewRouteRef,
  rootRouteRef,
} from '../../routes';
import { announcementsApiRef } from '../../apis';
import { useAnnouncements, useAnnouncementsTranslation } from '../../hooks';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Alert } from '@mui/lab';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles({
  newAnnouncementIcon: {
    minWidth: '36px',
  },
});

type AnnouncementsCardOpts = {
  title?: string;
  max?: number;
  category?: string;
  active?: boolean;
  variant?: InfoCardVariants;
  group?: string;
};

export const AnnouncementsCard = ({
  title,
  max,
  category,
  active,
  group,
  variant = 'fullHeight',
}: AnnouncementsCardOpts) => {
  const classes = useStyles();
  const announcementsApi = useApi(announcementsApiRef);
  const announcementsLink = useAnnouncementsRoute(rootRouteRef);
  const viewAnnouncementLink = useAnnouncementsRoute(announcementViewRouteRef);
  const createAnnouncementLink = useAnnouncementsRoute(
    announcementCreateRouteRef,
  );
  const lastSeen = announcementsApi.lastSeenDate();
  const { t } = useAnnouncementsTranslation();
  const { announcements, loading, error } = useAnnouncements({
    max: max || 5,
    category,
    active,
    selectedTab: group,
  });

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  const deepLink = {
    link: announcementsLink(),
    title: t('announcementsCard.seeAll'),
  };

  return (
    <InfoCard
      title={title || t('announcementsCard.announcements')}
      variant={variant}
      deepLink={deepLink}
    >
      <List dense>
        {announcements.results.map(announcement => (
          <ListItem key={announcement.id}>
            {lastSeen < DateTime.fromISO(announcement.created_at) && (
              <ListItemIcon
                className={classes.newAnnouncementIcon}
                title={t('announcementsCard.new')}
              >
                <NewReleasesIcon />
              </ListItemIcon>
            )}

            <ListItemText
              primary={
                <Link to={viewAnnouncementLink({ id: announcement.id })}>
                  {announcement.title}
                </Link>
              }
              secondary={
                <>
                  {DateTime.fromISO(announcement.created_at).toRelative()}
                  {announcement.category && (
                    <>
                      {` ${t('announcementsCard.in')} `}
                      <Link
                        to={`${announcementsLink()}?category=${announcement.category.slug}`}
                      >
                        {announcement.category.title}
                      </Link>
                    </>
                  )}{' '}
                  – {announcement.excerpt}
                </>
              }
            />
          </ListItem>
        ))}
        {announcements.count === 0 && (
          <ListItem>
            <ListItemText>
              {`${t('announcementsCard.noAnnouncements')} `}
              <Link to={createAnnouncementLink()}>
                {t('announcementsCard.addOne')}
              </Link>
              ?
            </ListItemText>
          </ListItem>
        )}
      </List>
    </InfoCard>
  );
};
